import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MergeTypeRoundedIcon from "@mui/icons-material/MergeTypeRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import WorkerData from "../../Types/WorkerData";

interface MergeWorkerDialogProps {
    sourceWorker: WorkerData;
    workers: WorkerData[];
    onClose: (mergeIntoWorkerId: string | null) => void;
}

const ACCENT = "#c79656";

function workerName(worker: WorkerData) {
    return `${worker.FirstName} ${worker.LastName}`.trim();
}

export default function MergeWorkerDialog({ sourceWorker, workers, onClose }: MergeWorkerDialogProps) {
    const [mounted, setMounted] = useState(false);
    const [dark, setDark] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedWorkerId, setSelectedWorkerId] = useState("");
    const [searchFocused, setSearchFocused] = useState(false);

    const searchRef = useRef<HTMLInputElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);
    const mergeRef = useRef<HTMLButtonElement>(null);

    const candidates = useMemo(
        () => workers
            .filter((worker) => worker.Guid !== sourceWorker.Guid)
            .sort((a, b) => workerName(a).localeCompare(workerName(b))),
        [sourceWorker.Guid, workers]
    );

    const filteredWorkers = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) return candidates;
        return candidates.filter((worker) => {
            const name = workerName(worker).toLowerCase();
            const username = worker.Username.toLowerCase();
            return name.includes(normalizedQuery) || username.includes(normalizedQuery);
        });
    }, [candidates, query]);

    const selectedWorker = candidates.find((worker) => worker.Guid === selectedWorkerId) ?? null;
    const isDark = dark;
    const canMerge = selectedWorker != null;

    useEffect(() => {
        requestAnimationFrame(() => setMounted(true));
        searchRef.current?.focus();

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
        const onColorSchemeChange = (e: MediaQueryListEvent) => setDark(e.matches);
        setDark(!!mq?.matches);
        mq?.addEventListener?.("change", onColorSchemeChange);

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose(null);
                return;
            }

            const isInput = (document.activeElement as HTMLElement | null)?.tagName === "INPUT";
            if (e.key === "Enter" && selectedWorkerId && !isInput) {
                e.preventDefault();
                onClose(selectedWorkerId);
                return;
            }

            if (e.key === "Tab") {
                const stops = [searchRef.current, cancelRef.current, mergeRef.current].filter(Boolean) as HTMLElement[];
                const active = document.activeElement as HTMLElement | null;
                const idx = stops.findIndex((el) => el === active);
                if (idx !== -1) {
                    e.preventDefault();
                    const next = e.shiftKey ? (idx - 1 + stops.length) % stops.length : (idx + 1) % stops.length;
                    stops[next].focus();
                }
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            mq?.removeEventListener?.("change", onColorSchemeChange);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [onClose, selectedWorkerId]);

    if (typeof document === "undefined") return null;

    const ring = (focused: boolean) => (focused ? "0 0 0 2px rgba(199,150,86,.28)" : "none");

    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        inset: 0,
        background: isDark ? "rgba(0,0,0,.6)" : "rgba(0,0,0,.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: mounted ? 1 : 0,
        transition: "opacity 140ms ease-out",
        zIndex: 9999,
    };

    const dialogStyle: React.CSSProperties = {
        width: "92vw",
        maxWidth: 620,
        maxHeight: "86vh",
        borderRadius: 18,
        background: isDark ? "#141414" : "#ffffff",
        color: isDark ? "#f5f5f5" : "#111111",
        border: "1px solid rgba(199,150,86,.35)",
        boxShadow: "0 16px 60px rgba(0,0,0,.35)",
        transform: mounted ? "translateY(0) scale(1)" : "translateY(6px) scale(.98)",
        transition: "transform 180ms ease-out",
        padding: 28,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 18,
    };

    const headerStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: 10,
        justifyContent: "center",
        marginTop: 2,
    };

    const titleStyle: React.CSSProperties = {
        margin: 0,
        fontSize: "1.2rem",
        lineHeight: 1.2,
    };

    const descStyle: React.CSSProperties = {
        margin: 0,
        textAlign: "center",
        lineHeight: 1.55,
        opacity: 0.86,
        fontSize: 14,
    };

    const searchWrapStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: 8,
        border: "1px solid rgba(199,150,86,.35)",
        borderRadius: 12,
        padding: "10px 12px",
        background: isDark ? "#101010" : "#ffffff",
        boxShadow: ring(searchFocused),
    };

    const searchInputStyle: React.CSSProperties = {
        flex: 1,
        border: 0,
        outline: "none",
        background: "transparent",
        color: isDark ? "#f5f5f5" : "#111111",
        fontSize: 14,
    };

    const listStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        overflowY: "auto",
        maxHeight: 280,
        paddingRight: 2,
    };

    const workerButtonStyle = (selected: boolean): React.CSSProperties => ({
        border: selected ? "1px solid rgba(199,150,86,.72)" : "1px solid rgba(199,150,86,.22)",
        borderRadius: 12,
        background: selected ? "rgba(199,150,86,.12)" : isDark ? "#101010" : "#ffffff",
        color: isDark ? "#f5f5f5" : "#111111",
        cursor: "pointer",
        padding: "12px 14px",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
    });

    const workerTextStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minWidth: 0,
    };

    const workerNameStyle: React.CSSProperties = {
        fontSize: 14,
        fontWeight: 700,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };

    const workerMetaStyle: React.CSSProperties = {
        fontSize: 12,
        opacity: 0.68,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };

    const selectedSummaryStyle: React.CSSProperties = {
        border: "1px solid rgba(199,150,86,.25)",
        borderRadius: 12,
        padding: "12px 14px",
        background: isDark ? "rgba(199,150,86,.08)" : "rgba(199,150,86,.06)",
        fontSize: 13,
        lineHeight: 1.45,
    };

    const actionsStyle: React.CSSProperties = {
        display: "flex",
        gap: 14,
        marginTop: 4,
    };

    const btnBase: React.CSSProperties = {
        border: 0,
        borderRadius: 12,
        padding: "12px 16px",
        fontWeight: 600,
        cursor: "pointer",
        outline: "none",
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
    };

    const cancelBtnStyle: React.CSSProperties = {
        ...btnBase,
        background: "transparent",
        color: isDark ? "#f5f5f5" : "#111111",
        border: "1px solid rgba(199,150,86,.45)",
    };

    const mergeBtnStyle: React.CSSProperties = {
        ...btnBase,
        background: canMerge ? ACCENT : "rgba(199,150,86,.45)",
        color: "#ffffff",
        cursor: canMerge ? "pointer" : "not-allowed",
        opacity: canMerge ? 1 : 0.9,
    };

    const dialog = (
        <div style={overlayStyle} onClick={() => onClose(null)} role="presentation">
            <div
                style={dialogStyle}
                role="dialog"
                aria-modal="true"
                aria-labelledby="merge-worker-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div style={headerStyle}>
                    <MergeTypeRoundedIcon style={{ color: ACCENT, fontSize: 20 }} />
                    <h2 id="merge-worker-title" style={titleStyle}>Merge Worker</h2>
                </div>

                <p style={descStyle}>
                    Merge {workerName(sourceWorker)} into another worker. The selected worker keeps the combined history and {workerName(sourceWorker)} will be removed.
                </p>

                <div style={searchWrapStyle}>
                    <SearchRoundedIcon style={{ color: ACCENT, fontSize: 18 }} />
                    <input
                        ref={searchRef}
                        type="text"
                        value={query}
                        placeholder="Search worker"
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                        style={searchInputStyle}
                    />
                </div>

                <div style={listStyle}>
                    {filteredWorkers.length > 0 ? filteredWorkers.map((worker) => {
                        const selected = worker.Guid === selectedWorkerId;
                        return (
                            <button
                                key={worker.Guid}
                                type="button"
                                onClick={() => setSelectedWorkerId(worker.Guid)}
                                style={workerButtonStyle(selected)}
                            >
                                <span style={workerTextStyle}>
                                    <span style={workerNameStyle}>{workerName(worker)}</span>
                                    <span style={workerMetaStyle}>{worker.Username ? `@${worker.Username}` : "No linked account"}</span>
                                </span>
                                {selected ? (
                                    <CheckRoundedIcon style={{ color: ACCENT, fontSize: 20, flexShrink: 0 }} />
                                ) : (
                                    <PersonSearchRoundedIcon style={{ color: isDark ? "#ddd" : "#333", opacity: 0.45, fontSize: 20, flexShrink: 0 }} />
                                )}
                            </button>
                        );
                    }) : (
                        <div style={{ border: "1px solid rgba(199,150,86,.22)", borderRadius: 12, padding: 18, textAlign: "center", opacity: 0.72, fontSize: 13 }}>
                            No workers found.
                        </div>
                    )}
                </div>

                {selectedWorker != null && (
                    <div style={selectedSummaryStyle}>
                        {workerName(sourceWorker)} will be merged into <strong>{workerName(selectedWorker)}</strong>.
                    </div>
                )}

                <div style={actionsStyle}>
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={() => onClose(null)}
                        style={cancelBtnStyle}
                    >
                        <CloseRoundedIcon style={{ color: ACCENT, fontSize: 17 }} />
                        Cancel
                    </button>

                    <button
                        ref={mergeRef}
                        type="button"
                        disabled={!canMerge}
                        onClick={() => selectedWorker != null && onClose(selectedWorker.Guid)}
                        style={mergeBtnStyle}
                    >
                        <MergeTypeRoundedIcon style={{ color: "#fff", fontSize: 18 }} />
                        Merge
                    </button>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(dialog, document.body);
}
